import { useMemo } from 'react';
import { useAmbientAnimations } from '../hooks/useAmbientAnimations';
import { useBackground } from '../hooks/useBackground';
import { cn } from '../utils/cn';
import { generateStarFieldBoxShadow, STAR_FIELD_SEED } from '../utils/spaceBackdropStars';

/**
 * `deep`    — galaxy/space screens. Radial gradient, no CSS star layer (the
 *             page's own animated `<Starfield>` canvas provides the travel
 *             effect instead), no race art.
 * `surface` — planet-side screens. Horizon-style linear gradient, CSS stars,
 *             race art at reduced opacity.
 * `neutral` — login, universe picker, race picker. Same gradient as
 *             `surface`; replaces the old `neutralBackgroundWrapper`.
 */
export type SpaceBackdropVariant = 'deep' | 'surface' | 'neutral';

export interface SpaceBackdropProps {
    variant: SpaceBackdropVariant;
}

const RADIAL_DEEP_GRADIENT =
    'radial-gradient(ellipse at 30% 20%, oklch(24% 0.05 280) 0%, oklch(13% 0.03 270) 45%, #050510 100%)';
const LINEAR_HORIZON_GRADIENT =
    'linear-gradient(180deg, #00050f 0%, #00051e 34%, #141232 62%, #14201e 82%, #3a1440 100%)';

const BASE_GRADIENT_BY_VARIANT: Record<SpaceBackdropVariant, string> = {
    deep: RADIAL_DEEP_GRADIENT,
    surface: LINEAR_HORIZON_GRADIENT,
    neutral: LINEAR_HORIZON_GRADIENT,
};

const SHOWS_STATIC_STARS_BY_VARIANT: Record<SpaceBackdropVariant, boolean> = {
    deep: false,
    surface: true,
    neutral: true,
};

const SHOWS_RACE_ART_BY_VARIANT: Record<SpaceBackdropVariant, boolean> = {
    deep: false,
    surface: true,
    neutral: true,
};

const STAR_COUNT = 220;

interface NebulaBloom {
    key: string;
    className: string;
    color: string;
    blurPx: number;
}

const NEBULA_BLOOMS: NebulaBloom[] = [
    {
        key: 'violet',
        className: 'absolute -top-24 -left-24 h-[480px] w-[480px] rounded-full',
        color: 'oklch(32% 0.43 720 / 16%)',
        blurPx: 126,
    },
    {
        key: 'teal',
        className: 'absolute -bottom-24 -right-24 h-[520px] w-[520px] rounded-full',
        color: 'oklch(5% 0.67 220 / 25%)',
        blurPx: 168,
    },
];

interface EdgeVignette {
    key: string;
    className: string;
    gradient: string;
}

const EDGE_VIGNETTES: EdgeVignette[] = [
    {
        key: 'left',
        className: 'absolute inset-y-0 left-0 w-[240px]',
        gradient: 'linear-gradient(90deg, rgba(5,5,15,.45), rgba(5,5,15,0))',
    },
    {
        key: 'right',
        className: 'absolute inset-y-0 right-0 w-[240px]',
        gradient: 'linear-gradient(270deg, rgba(5,5,15,.45), rgba(5,5,15,0))',
    },
];

/**
 * The single fixed atmosphere layer behind every screen (issue #182),
 * replacing the four places backgrounds used to be decided: the `body`
 * background in `index.css`, `App.tsx`'s inline `backgroundImage` +
 * `neutralBackgroundWrapper`, and the race-image lookup that used to be
 * wired straight into a wrapper div's style. `Starfield` (mounted
 * separately by `GalaxyPage`) is unaffected — its travel effect stays a
 * `/galaxy`-only feature layered in front of this backdrop's `deep` variant.
 *
 * Every layer here is `position: fixed; inset: 0; z-index: -1;
 * pointer-events: none` (the outer div), so it never affects layout and
 * never intercepts a click — see `space-backdrop.spec.ts` for the
 * click-through and `/galaxy` regression checks.
 */
export function SpaceBackdrop({ variant }: SpaceBackdropProps) {
    const { backgroundImage } = useBackground();
    const ambientAnimations = useAmbientAnimations();
    const starBoxShadow = useMemo(() => generateStarFieldBoxShadow(STAR_COUNT, STAR_FIELD_SEED), []);

    return (
        <div
            data-testid="space-backdrop"
            aria-hidden="true"
            className="fixed inset-0 z-[-1] pointer-events-none"
        >
            <div className="absolute inset-0" style={{ background: BASE_GRADIENT_BY_VARIANT[variant] }} />

            {SHOWS_STATIC_STARS_BY_VARIANT[variant] && (
                <div
                    data-testid="space-backdrop-stars"
                    className={cn('absolute top-0 left-0 h-px w-px rounded-full', ambientAnimations && 'animate-twinkle')}
                    style={{ boxShadow: starBoxShadow }}
                />
            )}

            {NEBULA_BLOOMS.map((bloom) => (
                <div
                    key={bloom.key}
                    data-testid={`space-backdrop-nebula-${bloom.key}`}
                    className={cn(bloom.className, ambientAnimations && 'animate-float-slow')}
                    style={{ background: bloom.color, filter: `blur(${bloom.blurPx}px)` }}
                />
            ))}

            {SHOWS_RACE_ART_BY_VARIANT[variant] && backgroundImage && (
                <div
                    data-testid="space-backdrop-race-art"
                    className="absolute inset-0 opacity-25"
                    style={{
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    }}
                />
            )}

            {EDGE_VIGNETTES.map((vignette) => (
                <div
                    key={vignette.key}
                    data-testid={`space-backdrop-vignette-${vignette.key}`}
                    className={vignette.className}
                    style={{ background: vignette.gradient }}
                />
            ))}
        </div>
    );
}

export default SpaceBackdrop;
